import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { HOST, IsEmployee, IsImpersonationMode, IsDSM, ShowPODShippingRates } from 'config/config';
import { useSelector } from 'react-redux';
import useToast from 'services/Hooks/useToast';
import tableDataQueryStringBuilder from 'services/Utilities/TableData';

const normalizeDealerIds = dealerIds => dealerIds?.filter(Boolean) ?? [];

export const usePrefetchCart = (isLogged, dealerId) => {
  const queryClient = useQueryClient();

  queryClient.prefetchQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      const shouldSendDealerId = IsImpersonationMode && !!dealerId;
      const response = await axios.get(
        HOST + '/api/dealer-portal/cart/active',
        shouldSendDealerId ? { params: { dealerId: dealerId } } : undefined,
      );
      return response.data;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: !!isLogged,
  });
};

export const useCarts = (tableDataParam, dealerIds = []) => {
  const { errorMessage } = useToast();
  const impersonationDealerId = useSelector(state => state.AccountReducer?.profile?.temporaryDealer_Id);
  const normalizedDealerIds = normalizeDealerIds(dealerIds);
  const requestParams = normalizedDealerIds.length ? { ...tableDataParam, dealerIds: normalizedDealerIds } : tableDataParam;

  const query = useQuery({
    queryKey: [
      'carts',
      JSON.stringify(tableDataParam),
      IsDSM || IsEmployee ? impersonationDealerId : 'default',
      normalizedDealerIds.join('|'),
    ],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/cart', {
        paramsSerializer: params => tableDataQueryStringBuilder(params),
        params: requestParams,
      });
      return response.data;
    },
    throwOnError: errorMessage,
    staleTime: 0,
  });
  return query;
};

export const useActiveCart = (isLogged, forcedFetch = false, dealerId = null) => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      const shouldSendDealerId = IsImpersonationMode && !!dealerId;
      const response = await axios.get(
        HOST + '/api/dealer-portal/cart/active',
        shouldSendDealerId ? { params: { dealerId: dealerId } } : undefined,
      );
      return response.data;
    },
    throwOnError: errorMessage,
    enabled: !!isLogged,
    staleTime: forcedFetch ? 0 : Infinity,
  });
  return query;
};

export const useCart = cartId => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['historyCart', cartId],
    queryFn: async () => {
      if (cartId) {
        const response = await axios.get(`${HOST}/api/dealer-portal/cart/${cartId}`);
        return response.data;
      } else {
        return null;
      }
    },
    throwOnError: errorMessage,
  });
  return query;
};

export const usePostCarItem = isDemo => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();

  const query = useMutation({
    mutationKey: ['addCartItem'],
    mutationFn: async cartItem => {
      return await axios.post(`${HOST}/api/dealer-portal/cart/items`, isDemo ? { ...cartItem, isDemo: true } : cartItem);
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostCartWall = isDemo => {
  const { saveMessage, errorMessage } = useToast();

  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addCartWall'],
    mutationFn: async cartWall => {
      return await axios.post(`${HOST}/api/dealer-portal/cart/walls`, isDemo ? { ...cartWall, isDemo: true } : cartWall);
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostCartTooling = isDemo => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addCartTooling'],
    mutationFn: async cartTooling => {
      return await axios.post(
        `${HOST}/api/dealer-portal/cart/toolings`,
        isDemo ? { ...cartTooling, isDemo: true } : cartTooling,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostCheckout = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addCartItem'],
    mutationFn: async ({ cartId, selectedFreightPodServiceCode }) => {
      return await axios.post(`${HOST}/api/dealer-portal/cart/${cartId}/checkout`, {
        selectedFreightPodServiceCode: selectedFreightPodServiceCode || null,
      });
    },
    onSuccess: () => {
      saveMessage();
      queryClient.setQueryData(['cart'], null);
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      queryClient.invalidateQueries({ queryKey: ['carts'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostChangeCartItemQuantity = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['changeQuantityCartItem'],
    mutationFn: async cartItem => {
      return await axios.post(`${HOST}/api/dealer-portal/cart/items/${cartItem.id}/quantity`, cartItem);
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostReplaceDepletedInventoriedCartItem = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['replaceDepletedInventoriedCartItem'],
    mutationFn: async ({ itemId }) => {
      return await axios.post(`${HOST}/api/dealer-portal/cart/items/${itemId}/replace-depleted-inventory`);
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostChangeCartToolingQuantity = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['changeQuantityCartTooling'],
    mutationFn: async cartTooling => {
      return await axios.post(`${HOST}/api/dealer-portal/cart/toolings/${cartTooling.id}/quantity`, cartTooling);
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchApplyPromoCode = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['applyPromoCode'],
    mutationFn: async cart => {
      return await axios.patch(`${HOST}/api/dealer-portal/cart/${cart.id}/promo`, cart);
    },
    onSuccess: ({ data }) => {
      if (data.message) {
        errorMessage(data);
      } else {
        saveMessage();
      }
      queryClient.setQueryData(['cart'], data.cart);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchChangeShipping = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['changeShipping'],
    mutationFn: async cart => {
      return await axios.patch(`${HOST}/api/dealer-portal/cart/${cart.id}/shipping`, cart);
    },
    onSuccess: () => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostCarrierRatePreview = () => {
  const query = useMutation({
    mutationKey: ['carrierRatePreview'],
    mutationFn: async ({ cartId }) => {
      if (!ShowPODShippingRates) {
        return null;
      }

      const response = await axios.post(`${HOST}/api/dealer-portal/cart/${cartId}/carrier-rate-preview`);
      return response.data;
    },
  });
  return query;
};

export const usePatchChangeDistrictSalesManager = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['changeDistrictSalesManager'],
    mutationFn: async cart => {
      return await axios.patch(`${HOST}/api/dealer-portal/cart/${cart.id}/district-sales-manager`, cart);
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchApproveCart = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['approveCart'],
    mutationFn: async cart => {
      return await axios.patch(`${HOST}/api/dealer-portal/cart/${cart.id}/status`, cart);
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['carts'] });
      queryClient.setQueryData(['historyCart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteCartItem = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteCartItem'],
    mutationFn: async cartItem => {
      return await axios.delete(`${HOST}/api/dealer-portal/cart/items/${cartItem.id}`);
    },
    onSuccess: ({ data }) => {
      deleteMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteCartWall = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteCartWall'],
    mutationFn: async cartWall => {
      return await axios.delete(`${HOST}/api/dealer-portal/cart/walls/${cartWall.id}`);
    },
    onSuccess: ({ data }) => {
      deleteMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteCartTooling = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteCartWall'],
    mutationFn: async cartTooling => {
      return await axios.delete(`${HOST}/api/dealer-portal/cart/toolings/${cartTooling.id}`);
    },
    onSuccess: ({ data }) => {
      deleteMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteCart = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteCart'],
    mutationFn: async cart => {
      return await axios.delete(`${HOST}/api/dealer-portal/cart/${cart.id}`);
    },
    onSuccess: () => {
      deleteMessage();
      queryClient.setQueryData(['cart'], null);
    },
    onError: errorMessage,
  });
  return query;
};

/* Section BRP CLUB */
export const usePostBrpClubCartItem = isDemo => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();

  const query = useMutation({
    mutationKey: ['addBrpClubCartItem'],
    mutationFn: async cartItem => {
      return await axios.post(
        `${HOST}/api/dealer-portal/brp-club/cart/items`,
        isDemo ? { ...cartItem, isDemo: true } : cartItem,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostBrpClubCartWall = isDemo => {
  const { saveMessage, errorMessage } = useToast();

  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addBrpClubCartWall'],
    mutationFn: async cartWall => {
      return await axios.post(
        `${HOST}/api/dealer-portal/brp-club/cart/walls`,
        isDemo ? { ...cartWall, isDemo: true } : cartWall,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostBrpClubCartTooling = isDemo => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addBrpClubCartTooling'],
    mutationFn: async cartTooling => {
      return await axios.post(
        `${HOST}/api/dealer-portal/brp-club/cart/toolings`,
        isDemo ? { ...cartTooling, isDemo: true } : cartTooling,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteBrpClubCartItem = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteBrpClubCartItem'],
    mutationFn: async cartItem => {
      return await axios.delete(`${HOST}/api/dealer-portal/brp-club/cart/items/${cartItem.id}`);
    },
    onSuccess: ({ data }) => {
      deleteMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteBrpClubCartWall = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteBrpClubCartWall'],
    mutationFn: async cartWall => {
      return await axios.delete(`${HOST}/api/dealer-portal/brp-club/cart/walls/${cartWall.id}`);
    },
    onSuccess: ({ data }) => {
      deleteMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteBrpClubCartTooling = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteBrpClubCartTooling'],
    mutationFn: async cartTooling => {
      return await axios.delete(`${HOST}/api/dealer-portal/brp-club/cart/toolings/${cartTooling.id}`);
    },
    onSuccess: ({ data }) => {
      deleteMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteBrpClubCart = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteBrpClubCart'],
    mutationFn: async cart => {
      return await axios.delete(`${HOST}/api/dealer-portal/brp-club/cart/${cart.id}`);
    },
    onSuccess: () => {
      deleteMessage();
      queryClient.setQueryData(['cart'], null);
    },
    onError: errorMessage,
  });
  return query;
};
